/**
 * EditorSaveService: persist a serialized scene manifest.
 *
 * Two strategies, tried in order:
 *   1. PUT /api/scenes/:sceneId/manifest  — when a backend is present it stores
 *      the manifest server-side so a refresh restores the designer's work.
 *   2. Download `<sceneId>.scene.json`     — fallback when no backend responds
 *      (the designer drops the file into public/assets/scenes/<id>/ manually).
 *
 * The download fallback always works, so saving never blocks the editor.
 */

import type { SceneManifest } from '../config/manifest';

export interface SaveResult {
    ok: boolean;
    /** Where the manifest ended up, for the editor to surface to the user. */
    message: string;
}

export class EditorSaveService {
    /** Try the server first; on any failure fall back to a browser download. */
    async save(sceneId: string, manifest: SceneManifest): Promise<SaveResult> {
        const body = JSON.stringify(manifest, null, 2);
        try {
            const res = await fetch(`/api/scenes/${encodeURIComponent(sceneId)}/manifest`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body
            });
            if (res.ok) {
                return { ok: true, message: `已保存到服务器 (PUT /api/scenes/${sceneId}/manifest)` };
            }
            console.warn(`[save] PUT 回退到下载（HTTP ${res.status}）`);
        } catch (err) {
            console.warn('[save] PUT 不可用，回退到下载：', err);
        }
        this.download(sceneId, manifest);
        return {
            ok: true,
            message: `服务器未就绪，已下载 ${sceneId}.scene.json — 放入 public/assets/scenes/${sceneId}/scene.json 即可生效`
        };
    }

    /** Always-download variant (the editor's "导出 JSON" action). */
    download(sceneId: string, manifest: SceneManifest): void {
        const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${sceneId}.scene.json`;
        a.click();
        URL.revokeObjectURL(url);
    }
}
